import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoConversionRpcClientContext } from "@redotech/redo-merchant-app-common/redo-conversion-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";

import { createContext, memo, useMemo } from "react";

/**
 * Tri-state describing how much of the Conversion Cloud a team can use.
 * - "none": team has no Conversion access at all (no secondary nav)
 * - "intent-only-small": self-serve Intent team whose estimated deal size is
 *   below the sales threshold (annualOrders × 30¢ < $5,000). They get a
 *   stripped-down sidebar (Dashboard + Spotlights only) and no upsell promo —
 *   we don't want a sales rep chasing them.
 * - "intent-only": self-serve Intent team large enough to be worth a sales
 *   conversation. Locked nav items + schedule-a-call CTA.
 * - "full": admin has flipped the Conversion toggle on; everything unlocked.
 */
export type ConversionAccessLevel =
  | "none"
  | "intent-only-small"
  | "intent-only"
  | "full";

/**
 * Coverage price assumed for the deal-size heuristic (per CRO-1430). The
 * ticket specifies a flat 30¢ per order, independent of the team's actual
 * configured coverage price, so this is intentionally a constant.
 */
export const COVERAGE_PRICE_CENTS = 30;

/**
 * Estimated annual revenue (in cents) below which a self-serve Intent team is
 * considered too small to justify a sales call. annualOrders × 30¢ < $5,000.
 */
export const INTENT_ONLY_SMALL_DEAL_SIZE_CENTS = 500_000;

/**
 * Pure derivation of access level from the underlying team flags + order
 * volume. Extracted so it can be unit-tested without mounting React.
 *
 * "full" wins over the intent-only branches when both flags are set — a team
 * that's been onboarded by sales should never show the locked UI even if
 * intentRequested was flipped earlier in their lifecycle.
 *
 * `annualOrders === null` (still loading or RPC failed) is treated as
 * "intent-only-small": we'd rather briefly hide the upsell from a borderline
 * team than briefly show it to a clearly-too-small one.
 */
export function deriveConversionAccessLevel(input: {
  checkoutOptimizationEnabled: boolean;
  intentRequested: boolean;
  annualOrders: number | null;
}): ConversionAccessLevel {
  if (input.checkoutOptimizationEnabled) {
    return "full";
  }
  if (!input.intentRequested) {
    return "none";
  }
  const estimatedDealSizeCents =
    (input.annualOrders ?? 0) * COVERAGE_PRICE_CENTS;
  if (estimatedDealSizeCents < INTENT_ONLY_SMALL_DEAL_SIZE_CENTS) {
    return "intent-only-small";
  }
  return "intent-only";
}

export interface ConversionSuiteStatuses {
  conversionEnabled: boolean;
  checkoutOptimizationEnabled: boolean;
  checkoutOutboundRatesEnabled: boolean;
  accessLevel: ConversionAccessLevel;
  /**
   * False while the order-count RPC that distinguishes "intent-only" from
   * "intent-only-small" is still in flight. During that window `accessLevel`
   * optimistically reads "intent-only-small" (see deriveConversionAccessLevel),
   * which is fine for the sidebar but NOT for surfaces that fire
   * page-view analytics — a large intent team would briefly mount the
   * intent-rescue upsell page and emit a spurious UPSELL_PAGE_VIEWED before
   * the RPC resolves them to "intent-only". Such surfaces should wait for
   * this to be true before committing to a tier.
   */
  accessLevelResolved: boolean;
}

export const ConversionSuiteStatusesContext = createContext<
  ConversionSuiteStatuses | undefined
>(undefined);

export const ConversionSuiteStatusesProvider = memo(
  function ConversionSuiteStatusesProvider({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const team = useRequiredContext(TeamContext);
    const conversionRpcClient = useRequiredContext(
      RedoConversionRpcClientContext,
    );
    const settings = team.settings;

    const checkoutOptimizationEnabled =
      !!settings.checkoutOptimization?.enabled;
    const checkoutOutboundRatesEnabled =
      !!team.shopifyIntegration?.checkoutOutboundRatesEnabled;
    const conversionEnabled =
      checkoutOptimizationEnabled && checkoutOutboundRatesEnabled;
    const intentRequested = !!settings.predictiveConversion?.intentRequested;

    // Only fetch when the team is actually in the intent-only branch — full
    // and none teams don't need the order count to decide their access level.
    const shouldFetchOrderCounts =
      !checkoutOptimizationEnabled && intentRequested;

    const orderCountsLoad = useLoad(async () => {
      if (!shouldFetchOrderCounts) {
        return null;
      }
      return await conversionRpcClient.getOnlineStoreOrderCounts();
    }, [conversionRpcClient, shouldFetchOrderCounts]);

    const annualOrders: number | null =
      orderCountsLoad.value && orderCountsLoad.value.success
        ? orderCountsLoad.value.lastYearCount
        : null;

    const accessLevel = deriveConversionAccessLevel({
      checkoutOptimizationEnabled,
      intentRequested,
      annualOrders,
    });

    // Resolved when we didn't need the RPC at all (none/full decided purely
    // from team flags) or the RPC has settled with a value or an error.
    const accessLevelResolved =
      !shouldFetchOrderCounts ||
      orderCountsLoad.value !== undefined ||
      orderCountsLoad.error !== undefined;

    const conversionSuiteStatuses = useMemo(
      () => ({
        conversionEnabled,
        checkoutOptimizationEnabled,
        checkoutOutboundRatesEnabled,
        accessLevel,
        accessLevelResolved,
      }),
      [
        conversionEnabled,
        checkoutOptimizationEnabled,
        checkoutOutboundRatesEnabled,
        accessLevel,
        accessLevelResolved,
      ],
    );

    return (
      <ConversionSuiteStatusesContext.Provider value={conversionSuiteStatuses}>
        {children}
      </ConversionSuiteStatusesContext.Provider>
    );
  },
);

export { default as POST_PURCHASE_UPSELL_ICON } from "@redotech/redo-icons/cursor-click-01.svg";
export { default as POST_PURCHASE_UPSELL_ICON_FILLED } from "@redotech/redo-icons/cursor-click-01_filled.svg";
export { default as CONVERSION_ICON } from "@redotech/redo-icons/shopping-cart-03.svg";
export { default as CONVERSION_ICON_FILLED } from "@redotech/redo-icons/shopping-cart-03_filled.svg";
